import { useCredits } from "@/contexts/CreditsContext";
import { useAdmin } from "@/hooks/useAdmin";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { getTierBrandLimitLabel, getTierDisplayName } from "@/lib/tierUtils";

interface UpgradeGateProps {
  /** The feature name to display in the lock message */
  featureName: string;
  /** Optional description of what the feature does */
  description?: string;
  /**
   * Lowest DB tier allowed through. Defaults to "solopreneur", i.e. any paid plan,
   * which is what every existing caller relied on.
   *
   * Pass "reseller" for a Solopreneur-only page. Kate found (VA-215) that /agency
   * was reachable by BOTH Free and Starter: the route had no gate at all, and this
   * component would not have caught Starter anyway, because it only ever blocked
   * the free plan.
   *
   * Remember the offset: DB "solopreneur" is the plan shown as Starter, and DB
   * "reseller" is the plan shown as Solopreneur.
   */
  minTier?: "solopreneur" | "reseller";
  /** The wrapped content to show if the user has access */
  children: React.ReactNode;
}

const TIER_RANK: Record<string, number> = { basic: 0, solopreneur: 1, reseller: 2 };

/**
 * Wraps a feature page and shows an upgrade prompt if the user is on the free (basic) plan.
 * Free users only have access to the Brand Guide — everything else is gated.
 */
export function UpgradeGate({ featureName, description, minTier = "solopreneur", children }: UpgradeGateProps) {
  const { credits, loading } = useCredits();
  const { isAdmin, loading: adminLoading } = useAdmin();

  const rank = TIER_RANK[credits?.subscription_tier ?? "basic"] ?? 0;
  // Admins are not blocked by plan.
  //
  // vision-chat already works this way — its `previewAllowed` branch lets an admin past both
  // is_published and min_tier, on the reasoning that min_tier says who is ENTITLED to a feature
  // and staff testing it already are. The client did not agree, so an admin on a lower plan hit
  // this wall and never reached the page the server would have served them. That is not
  // hypothetical: it is why the QA account, an admin on the free plan, could not open the
  // Assistant to test admin-only skills at all.
  //
  // This is not a security boundary and never was. Live Coaching's real gate is server-side
  // (VA-101: a UI-only check was bypassed once already) — the edge functions and the HLS token
  // still decide. So an admin below the plan can now reach that page and will still be refused
  // the stream, which is the honest behavior: the UI stops pretending to be the lock.
  const locked =
    !loading && !adminLoading && !isAdmin && credits !== null && rank < TIER_RANK[minTier];

  // Still loading, an admin, or the plan is high enough — render the page.
  if (loading || adminLoading || !locked) {
    return <>{children}</>;
  }

  // Below the required plan — show the upgrade prompt
  return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="max-w-lg w-full text-center space-y-6 px-4">
          {/* Lock icon */}
          <div className="mx-auto w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Lock className="h-10 w-10 text-primary" />
          </div>

          {/* Title */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">
              {featureName} Requires an Upgrade
            </h1>
            <p className="text-muted-foreground leading-relaxed">
              {description ||
                `To access ${featureName}, you need to upgrade to the ${getTierDisplayName("solopreneur")} or ${getTierDisplayName("reseller")} plan.`}
            </p>
          </div>

          {/* Bridget, 2026-08-05, with a screenshot of this exact section:
              "Don't forget this section too." Starter ($27) was retired from sale,
              so this used to be a two-card comparison and is now the one plan a
              member can actually choose. See docs/bridget/001-starter-retired.md. */}
          <div className="pt-2">
            <div className="rounded-xl border border-[hsl(var(--brand-deep-purple))]/20 bg-[hsl(var(--brand-deep-purple))]/5 p-5 text-left space-y-2 max-w-sm mx-auto">
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-[hsl(var(--brand-deep-purple))]" />
                <span className="font-semibold text-sm text-foreground">{getTierDisplayName("reseller")}</span>
              </div>
              <p className="text-2xl font-bold text-foreground">$147<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
              <ul className="text-xs text-muted-foreground space-y-1">
                {/* Was "Everything in Starter" — a plan nobody can see any more. */}
                <li>✓ Full Content Workflow</li>
                <li>✓ 500 AI Credits</li>
                <li>✓ {getTierBrandLimitLabel("reseller")}</li>
                <li>✓ Live Coaching 4x/Week</li>
              </ul>
            </div>
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button asChild className="gradient-button text-primary-foreground gap-2">
              <Link to="/plan">
                View Plans & Upgrade
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/brand-guide">
                Go to Brand Guide
              </Link>
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Your free plan includes access to the Brand Guide with 1 brand.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}
